import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import DropdownBase from '../DropdownBase';
import { st, classes } from './PopoverMenu.st.css';
import {
  listItemSectionBuilder,
  ListItemSectionTypes,
} from '../ListItemSection';
import {
  PopoverMenuComponent,
  PopoverMenuItemProps,
  PopoverMenuProps,
  PopoverMenuSectionTitleProps,
  PopoverTriggerElementProps,
  PopoverMenuRefHandle,
} from './PopoverMenu.types';
import { PLACEMENT, TEXT_SIZES } from './PopoverMenu.constants';
import { APPEND_TO } from '../PopoverNext/PopoverNext.constants';
import { mergeRefs } from '../utils/mergeRefs';

const DEFAULT_MAX_WIDTH_PX = 204;
const DEFAULT_MIN_WIDTH_PX = 144;

/** PopoverMenu */
export const PopoverMenu = forwardRef<PopoverMenuRefHandle, PopoverMenuProps>(
  function PopoverMenu(props: PopoverMenuProps, forwardedRef) {
    const {
      maxWidth = DEFAULT_MAX_WIDTH_PX,
      minWidth = DEFAULT_MIN_WIDTH_PX,
      placement = PLACEMENT.bottom,
      appendTo = APPEND_TO.window,
      textSize = TEXT_SIZES.medium,
      fixed = false,
      flip = true,
      showArrow = true,
      ellipsis = false,
      maxHeight = 'auto',
      triggerElement,
      children,
      dataHook,
      moveBy,
      zIndex,
      className,
      fluid,
      onHide,
      onShow,
      fixedFooter,
    } = props;

    const dropdownRef = useRef<DropdownBase>(null);

    useImperativeHandle(forwardedRef, () => ({
      _open: () => {
        if (dropdownRef.current) {
          dropdownRef.current._open();
        }
      },
    }));

    const filterChildren = (
      children: React.ReactNode,
    ): React.ReactElement[] => {
      return (
        React.Children.map(children, child => child)?.filter(
          child =>
            typeof child !== 'string' && !!child && React.isValidElement(child),
        ) ?? []
      );
    };

    const buildOptions = (children: React.ReactElement[]) => {
      return children.map((child, id) => {
        const displayName =
          child.type && (child.type as React.ComponentType).displayName;

        if (displayName && displayName === 'PopoverMenu.Divider') {
          return {
            id,
            type: 'divider' as ListItemSectionTypes,
            dataHook: child.props.dataHook,
          };
        }

        if (displayName && displayName === 'PopoverMenu.SectionTitle') {
          return {
            id,
            type: 'title' as ListItemSectionTypes,
            title: child.props.title,
            dataHook: child.props.dataHook,
          };
        }

        if (displayName && displayName === 'PopoverMenu.MenuItem') {
          return {
            id,
            title: child.props.text,
            onClick: child.props.onClick,
            skin: child.props.skin,
            dataHook: child.props.dataHook,
            prefixIcon: child.props.prefixIcon,
            disabled: child.props.disabled,
            disabledDescription: child.props.disabledDescription,
            subtitle: child.props.subtitle,
            suffixIcon: child.props.suffixIcon,
          };
        }

        return {
          id,
          value: child,
          type: 'custom',
          overrideStyle: true,
        };
      });
    };

    const renderOptions = () => {
      const filteredChildren = filterChildren(children);
      const options = buildOptions(filteredChildren);

      return options.map(option => {
        const { type, ...optionRest } = option;

        // Custom
        if (type === 'custom') {
          return optionRest;
        }

        // Divider
        if (type === 'divider') {
          return listItemSectionBuilder({
            ...optionRest,
            type: option.type as ListItemSectionTypes,
          });
        }

        // SectionTitle
        if (type === 'title') {
          return listItemSectionBuilder({
            ...optionRest,
            type: option.type as ListItemSectionTypes,
          });
        }

        const { id, disabled, dataHook, skin, subtitle, title, ...rest } =
          optionRest;

        return {
          ...rest,
          id,
          disabled,
          as: 'button',
          dataHook: dataHook || `popover-menu-${id}`,
          skin: skin || 'standard',
          size: textSize,
          className: classes.listItem,
          ellipsis,
          subtitle,
          shouldFocusWithoutScroll: true,
          value: 'action',
          title: false,
          optionTitle: title,
        };
      });
    };

    const renderTriggerElement = ({
      toggle,
      open,
      close,
      isOpen,
      ref,
    }: PopoverTriggerElementProps) => {
      if (!triggerElement) {
        return null;
      }

      const handleTriggerClick = (e: React.MouseEvent<HTMLElement>) => {
        if (
          React.isValidElement(triggerElement) &&
          triggerElement.props?.onClick
        ) {
          triggerElement.props.onClick(e);
        }
        toggle();
      };

      const commonProps = {
        onClick: handleTriggerClick,
        ref,
        'aria-haspopup': 'menu',
        'aria-expanded': isOpen,
      };

      // When cloning a React element, merge the user's ref with the internal ref
      if (React.isValidElement(triggerElement)) {
        const elementWithRef = triggerElement as React.ReactElement & {
          ref?: PopoverTriggerElementProps['ref'];
        };
        const userRef = elementWithRef.ref;
        const mergedRef = mergeRefs(ref, userRef);

        return React.cloneElement(triggerElement, {
          ...commonProps,
          ref: mergedRef,
        } as any);
      }

      // When using a function, pass the ref directly
      if (typeof triggerElement === 'function') {
        return (triggerElement as React.FC)({
          toggle,
          open,
          close,
          isOpen,
          ...commonProps,
        });
      }

      return null;
    };

    /**
     * If the trigger element is disabled, the popover menu should not trigger open or close.
     * Otherwise, the popover menu should use DropdownBase's default behavior.
     */
    const getDropdownOpenProps = () => {
      // Not bulletproof. We should use popovermenu disabled prop instead.
      // TODO: introduce disabled prop for popovermenu.
      let isDropdownDisabled = false;
      if (React.isValidElement(triggerElement)) {
        isDropdownDisabled = triggerElement.props?.disabled ?? false;
      }
      return isDropdownDisabled ? { open: false } : {};
    };

    return (
      <DropdownBase
        {...getDropdownOpenProps()}
        ref={dropdownRef}
        className={st(classes.root, className)}
        popoverContentClassName={st(classes.popoverContent, {
          withWidth: Boolean(minWidth || maxWidth),
        })}
        dataHook={dataHook}
        animate
        options={renderOptions()}
        appendTo={appendTo}
        placement={placement}
        minWidth={minWidth}
        maxWidth={maxWidth}
        width="fit-content"
        flip={flip}
        fixed={fixed}
        showArrow={showArrow}
        tabIndex={-1}
        moveBy={moveBy}
        maxHeight={maxHeight}
        zIndex={zIndex}
        fluid={fluid}
        onHide={onHide}
        onShow={onShow}
        listType="action"
        autoFocus
        fixedFooter={fixedFooter}
      >
        {({ toggle, open, close, isOpen, ref }) =>
          renderTriggerElement({ toggle, open, close, isOpen, ref })
        }
      </DropdownBase>
    );
  },
);

const MenuItem: React.FC<PopoverMenuItemProps> = () => <></>;
MenuItem.displayName = 'PopoverMenu.MenuItem';

const Divider: React.FC = () => <></>;
Divider.displayName = 'PopoverMenu.Divider';

const SectionTitle: React.FC<PopoverMenuSectionTitleProps> = () => <></>;
SectionTitle.displayName = 'PopoverMenu.SectionTitle';

Object.assign(PopoverMenu, {
  displayName: 'PopoverMenu',
  MenuItem,
  Divider,
  SectionTitle,
});

export default PopoverMenu as PopoverMenuComponent;
